import React, {useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {useIntl} from "react-intl";
import {Icon, TextField} from "peggirkit";
import {Route} from "@/routing/Router";
import {api} from "@/data/api";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {ADMIN_MENU_QUERY_KEY} from "@/data/entities/menuEditor";
import {OptionGroup} from "@/data/entities/option";
import OptionGroupsOverviewHeading from "@widgets/menuEditor/optionGroups/heading/OptionGroupsOverviewHeading";
import OptionGroupsTable from "@widgets/menuEditor/optionGroups/table/OptionGroupsTable";
import EditOptionGroupModal, {
    EditOptionGroupModalConfig,
} from "@widgets/menuEditor/optionGroups/edit/EditOptionGroupModal";
import {
    createOptionGroupsLookup,
    OptionGroupsLookup,
    sortOptionGroups,
} from "@widgets/menuEditor/optionGroups/optionGroupsLookup";
import IconAddOn from "peggirkit/dist/components/Input/Field/TextField/TextFieldAddOn/IconAddOn";
import {MagnifyingGlassIcon} from "@heroicons/react/20/solid";
import ButtonAddOn from "peggirkit/dist/components/Input/Field/TextField/TextFieldAddOn/ButtonAddOn";
import {XMarkIcon} from "@heroicons/react/16/solid";

type Props = {
    route: Route,
};

const OptionGroupsOverview = ({route}: Props) => {
    const intl = useIntl();
    const {dashboardLocation} = useRestaurant();
    const [search, setSearch] = useState("");
    const [editModal, setEditModal] = useState<EditOptionGroupModalConfig>({show: false, group: null});
    const {data: menu, isFetching} = useQuery({
        queryKey: [ADMIN_MENU_QUERY_KEY, dashboardLocation.id],
        queryFn: () => api.menuEditor.getMenu(dashboardLocation.id),
    });
    const optionGroups: OptionGroup[] = useMemo(() => {
        if (menu === undefined) return [];
        const query = search.trim().toLowerCase();
        return sortOptionGroups(menu.optionGroups)
            .filter(group => query === "" || group.name.toLowerCase().includes(query));
    }, [menu, search]);
    const lookup: OptionGroupsLookup | null = useMemo(
        () => menu === undefined ? null : createOptionGroupsLookup(menu),
        [menu]);

    return (
        <>
            <OptionGroupsOverviewHeading
                route={route}
                isFetching={isFetching}
                onNew={() => setEditModal({show: true, group: null})}
            />

            <div className={"mt-6 md:mt-12 w-full flex flex-col gap-4"}>
                <TextField
                    containerClassname={"w-full max-w-sm"}
                    type={"text"}
                    placeholder={intl.formatMessage({id: "searchOptionGroups"})}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    leading={IconAddOn({
                        icon: <Icon icon={MagnifyingGlassIcon}/>
                    })}
                    trailing={
                        search.length === 0
                            ? undefined
                            : ButtonAddOn({
                                position: "trailing",
                                buttonProps: {
                                    iconOnly: true,
                                    iconBefore: <Icon icon={XMarkIcon}/>,
                                    buttonAction: {
                                        as: "button",
                                        actionProps: {
                                            onClick: e => {
                                                e.preventDefault();
                                                setSearch("");
                                            },
                                        },
                                    },
                                },
                            })
                    }
                />

                {
                    lookup !== null &&
                    <OptionGroupsTable
                        optionGroups={optionGroups}
                        lookup={lookup}
                        isFetching={isFetching}
                        onEdit={(group: OptionGroup) => setEditModal({show: true, group})}
                    />
                }
            </div>

            {
                menu !== undefined && lookup !== null &&
                <EditOptionGroupModal
                    config={editModal}
                    setConfig={setEditModal}
                    lookup={lookup}
                />
            }
        </>
    );
};

export default OptionGroupsOverview;